import {useEffect, useEffectEvent, useState} from 'react';
import assert from 'assert-ts';
import {Concept} from 'types';
import type {Data, Schema, Valid} from 'schemaDefinition/types';
import type {
  CodeList,
  CodeListId,
  CodeListMap,
  LocalCodeListId,
} from 'services/codelists/types';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {useRestrictedCodelistsContext} from 'services/codelists';
import {useConditionalManifestationEdit} from 'services/metadata';
import {validate} from 'schema/form/functions/validators/validate';
import {useSchemaRefCodelists} from 'schemas';

const getLocalCodelists = (
  localIds: LocalCodeListId[],
  getLocalCodelist: (id: CodeListId) => CodeList | undefined,
): CodeListMap => {
  return localIds.reduce<CodeListMap>((acc, id) => {
    const codelist = getLocalCodelist(id);
    if (codelist) {
      acc[id] = codelist;
    }
    return acc;
  }, {});
};

export const useManifestationValidation = (
  manifestationid: string,
  relatedScope: Data,
  showErrors: boolean,
  schema: Schema,
): Valid | undefined => {
  // Optimization: Only select manifestation if we are showing errors
  const manifestation = useConditionalManifestationEdit(
    manifestationid,
    showErrors,
  );
  const codelistMap = useSchemaRefCodelists(Concept.manifestation);
  const {getLocalCodelist} = useRestrictedCodelistsContext();

  const [valid, setValid] = useState<Valid | undefined>(undefined);
  const onSetValid = useEffectEvent(setValid);

  useEffect(() => {
    if (showErrors) {
      const localCodeLists = getLocalCodelists(
        AllLocalCodeListIds,
        getLocalCodelist,
      );

      const allCodelistsMap = {
        ...codelistMap,
        ...localCodeLists,
      };

      const validation = validate(
        assert(manifestation, 'useManifestationValidation: no manifestation'),
        relatedScope,
        schema,
        allCodelistsMap,
      );

      onSetValid(validation.valid);
    } else {
      onSetValid(undefined);
    }
  }, [
    manifestation,
    schema,
    codelistMap,
    showErrors,
    relatedScope,
    getLocalCodelist,
  ]);

  return valid;
};
